import publicUrls from "../../../routes/publicUrls";
import React, { useEffect, useState } from "react";

import { getUser } from "../../../Model/LocalStorage";
import { encodeId } from "../../../hash";
import WITHDRAWN_IMAGE from "../../../images/Banner/withdrawn.jpg"; // same asset the lot list uses
import Section from "../../../components/gallery/Section";
import SectionHeader from "../../../components/gallery/SectionHeader";
import CardRail from "../../../components/gallery/CardRail";
import { ArtworkCard } from "../../../components/gallery/Cards";
import { auctionsFiltering } from "../../../Model/Auction";
import { fetchFavoriteLots } from "../homeData";
import { htmlToText } from "../utils";



// Auction type + status combinations to search for favourites (in this order).
// auction_status codes used by the API: 1 and 2 = upcoming / ongoing auctions.
const AUCTION_SOURCES = [
  ["LIVE_AUCTION", 1],
  ["LIVE_AUCTION", 2],
  ["ONLINE_AUCTION", 1],
  ["ONLINE_AUCTION", 2],
];
// Safety net on the returned rows: accept the status as a code (1/2) or as text.
const ALLOWED_STATUSES = [1, 2, "1", "2", "UPCOMING", "ONGOING"];
// Favourites are looked up in at most this many auctions (one small request each).
const MAX_AUCTIONS = 12;
const MAX_LOTS = 16;
const LOT_IMAGE_FALLBACK = "https://placehold.co/400"; // same fallback the lot list uses

// Same title clean-up the lot list uses when it builds a lot link.
function cleanTitle(title) {
  return title === null || title === undefined ? "untitle" : String(title).replace(/[^a-zA-Z0-9-_]/g, "");
}

function lotUrl(lot, auctionSlug) {
  const d = lot.data;
  return publicUrls.lot(auctionSlug, encodeId(d.auction_id), d.lot_number, cleanTitle(d.title));
}

function lotImage(lot) {
  if (lot.data.isWithdrawn === 1) return WITHDRAWN_IMAGE;
  return lot.images?.[0]?.img_url || lot.image?.[0]?.img_url || LOT_IMAGE_FALLBACK;
}

function highestBid(lot) {
  const amounts = Array.isArray(lot.myBid) ? lot.myBid.map((b) => Number(b.amount)).filter((n) => !Number.isNaN(n)) : [];
  return amounts.length ? Math.max(...amounts) : null;
}

// Same "reserve not met" statuses the lot list checks.
const RESERVE_NOT_MET_STATUSES = ["RESERVE_NOT_MET", "PASS", "UNSOLD"];

/** Bid status for the card, or null when the lot list would not show one either. */
function bidStatus(lot, userId, bid) {
  const d = lot.data;
  if (bid === null || d.isWithdrawn === 1 || !(d.hp > 0)) return null;
  const amount = `₱${bid.toLocaleString()}`;
  if (userId === d.userBid) {
    if (RESERVE_NOT_MET_STATUSES.includes(d.lot_status)) {
      return { tone: "negative", label: `Reserve not met · Your bid ${amount}` };
    }
    if (d.lot_status === "SOLD") {
      return { tone: "positive", label: `You won this lot · ${amount}` };
    }
    return { tone: "positive", label: `The bid is with you · ${amount}` };
  }
  return { tone: "negative", label: `You have been outbid · Your bid ${amount}` };
}


function currentBidLabel(lot) {
  const d = lot.data;
  if (d.isDone === 1 || d.isWithdrawn === 1 || !(d.hp > 0)) return null;
  return `Current bid: ₱${Number(d.hp).toLocaleString()}`;
}

// Lots come from the favourites query (fav=<userId>); honour data.fav when the API sends it.
function isFavorite(lot) {
  const fav = lot.data.fav;
  return fav === undefined || fav === null ? true : Boolean(fav) && fav !== "0";
}

function isSignedIn() {
  try {
    const token = localStorage.getItem("token");
    return Boolean(token) && token !== "null" && token !== "undefined";
  } catch {
    return false;
  }
}

function ActivitySection() {
  const user = getUser();
  const userId = user?.id;
  const signedIn = isSignedIn() && Boolean(userId);

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(signedIn);

  useEffect(() => {
    if (!signedIn) return undefined;
    let isActive = true;

    (async () => {
      try {
        // Load the four auction lists in parallel, then merge them (keeping the order above)
        // and drop duplicates by auction id.
        const lists = await Promise.all(
          AUCTION_SOURCES.map(([auctionType, status]) => auctionsFiltering(auctionType, status))
        );
        const seen = new Set();
        const list = lists
          .map((rows) => (Array.isArray(rows) ? rows : [])) // auctionsFiltering returns undefined on error
          .flat()
          // Only upcoming / ongoing auctions (the API already filters by status; this is a safety net).
          .filter((a) => !a?.auction_status || ALLOWED_STATUSES.includes(a.auction_status))
          .filter((a) => a && a.id !== undefined && !seen.has(a.id) && seen.add(a.id))
          .slice(0, MAX_AUCTIONS);

        const perAuction = await Promise.all(
          list.map(async (a) => {
            const lots = await fetchFavoriteLots(a.id, userId);
            return lots
              .filter((lot) => lot && lot.data && lot.data.lot_number !== undefined)
              .map((lot) => ({ lot, auction: a }));
          })
        );

        if (isActive) setItems(perAuction.flat().slice(0, MAX_LOTS));
      } catch (error) {
        console.error("Failed to load your activity:", error);
      } finally {
        if (isActive) setLoading(false);
      }
    })();

    return () => {
      isActive = false;
    };
  }, [signedIn, userId]);

  // Hide the whole section: not signed in, still loading, or nothing to show.
  if (!signedIn || loading || items.length === 0) return null;

  return (
    <Section labelledBy="home-activity-title" sx={{ borderBottom: "1px solid #E4E1DB" }}>
      <SectionHeader
        id="home-activity-title"
        eyebrow="Your activity"
        title="Bids & Favorites"
        description="Lots you've saved in current auctions, with your highest bid where you've placed one."
        actionLabel="View all"
        actionHref="/my/account"
      />

      <CardRail label="your favourite lots" perView={{ sm: 2, md: 3, lg: 4 }} mobileWidth="44%">
        {items.map(({ lot, auction: a }) => {

          const bid = highestBid(lot);
          const status = bidStatus(lot, userId, bid);
          const artist = htmlToText(lot.data.artist);
          const title = htmlToText(lot.data.title);
          return (
            <ArtworkCard
              key={`${lot.data.auction_id}-${lot.data.lot_number}`}
              href={lotUrl(lot, a.c_slug)}
              image={lotImage(lot)}
              imageAlt={[artist, title].filter(Boolean).join(", ") || `Lot ${lot.data.lot_number}`}
              eyebrow={[`Lot ${lot.data.lot_number}`, a.d_name].filter(Boolean).join(" · ")}
              artist={artist}
              title={title}
              favorite={isFavorite(lot)}
              status={status}
              meta={
                currentBidLabel(lot) ||
                (status ? null : bid !== null ? `Your highest bid: ₱${bid.toLocaleString()}` :  `₱${lot.data.starting_price.toLocaleString()}`)
              }
            />
          );
        })}
      </CardRail>
    </Section>
  );
}

export default React.memo(ActivitySection);